// Deeplinks in den Gambio-Shop (Frontend-Artikelseite + Admin-Backend). Die Basis-URL kommt
// pro Shop aus `gambio_list_shops().base_url` (die im Shop hinterlegte Gambio-Installation,
// z. B. https://gambio.goedev.de) - NICHT der Host dieser Connector-App.
//
// Wichtig: Die Marktplatz-App läuft im Vario-iframe (Sandbox ohne allow-popups) → `window.open`
// tut nichts; Links MÜSSEN als `<a target="_blank" rel="noopener">` gerendert werden.

/** base_url normalisieren: trailing Slashes entfernen. null/leer → null. */
function normalizeBase(baseUrl: string | null | undefined): string | null {
  if (!baseUrl) return null;
  const trimmed = baseUrl.trim().replace(/\/+$/, '');
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Gambio-Produkt-ID aus der `marketplace_unit_id`. Flache Produkte: die ID selbst (z. B. "15").
 * Varianten haben die Form "<pId>-v<vId>" (z. B. "1-v3") → die Eltern-Produkt-ID "1" (Varianten
 * haben keine eigene Shop-/Backend-Seite, der Link zeigt aufs Elternprodukt).
 */
export function gambioProductId(idItem: string): string {
  const dashV = idItem.indexOf('-v');
  return dashV > 0 ? idItem.slice(0, dashV) : idItem;
}

/** Frontend-Artikelseite im Shop: `<base>/product_info.php?info=p<pId>`. null wenn keine base_url. */
export function gambioProductFrontendUrl(baseUrl: string | null | undefined, productId: string): string | null {
  const base = normalizeBase(baseUrl);
  if (!base || !productId) return null;
  return `${base}/product_info.php?info=p${encodeURIComponent(productId)}`;
}

/**
 * Admin-Backend-Artikelseite: `<base>/admin/categories.php?page=1&cPath=0&pID=<pId>&action=new_product`.
 * (Gambio öffnet damit den Produkt-Editor des bestehenden Artikels.) null wenn keine base_url.
 */
export function gambioProductAdminUrl(baseUrl: string | null | undefined, productId: string): string | null {
  const base = normalizeBase(baseUrl);
  if (!base || !productId) return null;
  return `${base}/admin/categories.php?page=1&cPath=0&pID=${encodeURIComponent(productId)}&action=new_product`;
}

/**
 * Admin-Backend-Währungsverwaltung. null wenn keine base_url.
 */
export function gambioCurrencyAdminUrl(baseUrl: string | null | undefined): string | null {
  const base = normalizeBase(baseUrl);
  if (!base) return null;
  // Gambio-Admin-Legacy-Route (konsistent mit gambioProductAdminUrl → categories.php).
  return `${base}/admin/currencies.php`;
}

/**
 * Admin-Backend, Konfigurationsseite mit dem Bereich „Kunden" — dort sitzt der Schalter
 * „Kundengruppencheck" (`GROUP_CHECK`). Route vom Betreiber genannt (2026-08-21):
 * `<base>/admin/configurations#category-customers`. Der Fragment-Teil steuert das aufgeklappte
 * Panel und darf NICHT URL-encodiert werden. null wenn keine base_url.
 */
export function gambioCustomerConfigAdminUrl(baseUrl: string | null | undefined): string | null {
  const base = normalizeBase(baseUrl);
  if (!base) return null;
  return `${base}/admin/configurations#category-customers`;
}

/**
 * Admin-Backend: Zahlungsarten des Shops.
 * `<base>/admin/admin.php?do=HubConfiguration/PaymentMethods`
 *
 * Das `?do=`-Muster ist Gambios Hub-Konfiguration; das Fragezeichen und der Schrägstrich gehören
 * zur Route und werden NICHT kodiert (analog zum Fragment beim Kundengruppencheck-Link).
 * null wenn keine base_url.
 */
export function gambioPaymentMethodsAdminUrl(baseUrl: string | null | undefined): string | null {
  const base = normalizeBase(baseUrl);
  return base ? `${base}/admin/admin.php?do=HubConfiguration/PaymentMethods` : null;
}

/**
 * Admin-Backend: Steuersätze des Shops. `<base>/admin/tax_rates.php`
 * Route vom Betreiber genannt (2026-08-25). null wenn keine base_url.
 */
export function gambioTaxRatesAdminUrl(baseUrl: string | null | undefined): string | null {
  const base = normalizeBase(baseUrl);
  return base ? `${base}/admin/tax_rates.php` : null;
}

/**
 * Admin-Backend: Versandmodule (Paketdienste) des Shops.
 * `<base>/admin/modules.php?set=shipping`
 * null wenn keine base_url.
 */
export function gambioShippingModulesAdminUrl(baseUrl: string | null | undefined): string | null {
  const base = normalizeBase(baseUrl);
  return base ? `${base}/admin/modules.php?set=shipping` : null;
}

/**
 * Admin-Backend: Zaehlerkreise des Shops (Startwerte fuer Kunden-, Bestell- und Rechnungsnummern).
 * `<base>/admin/gm_id_starts.php`
 *
 * Gegenstueck zum VARIO-Zaehlerkreis-Link (`varioCountersUrl`): wer die beiden Nummernkreise
 * gegeneinander abgrenzt, braucht beide Seiten. Route vom Betreiber genannt (2026-08-26).
 * null wenn keine base_url.
 */
export function gambioIdStartsAdminUrl(baseUrl: string | null | undefined): string | null {
  const base = normalizeBase(baseUrl);
  return base ? `${base}/admin/gm_id_starts.php` : null;
}
